import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  createCampaign,
  getRecipientsPreview,
} from "../../../redux/actions/campaignsAction";
import {
  createRightsRequest,
  listRightsRequests,
  updateRightsRequest,
  fulfilRightsRequest,
} from "../../../redux/actions/rightsRequestsAction";
import { downloadConsentExportCsv } from "../../../redux/actions/consentAction";

const { Title, Text, Paragraph } = Typography;

const RIGHTS_REQUEST_TYPES = [
  { value: "access", label: "Access" },
  { value: "correction", label: "Correction" },
  { value: "erasure", label: "Erasure" },
  { value: "withdrawal", label: "Withdrawal" },
];

const STATUS_COLORS = {
  open: "blue",
  in_progress: "orange",
  fulfilled: "green",
  rejected: "default",
};

const DAY_MS = 24 * 60 * 60 * 1000;

function dueColor(request) {
  if (request.status === "fulfilled" || request.status === "rejected") {
    return "default";
  }
  const daysLeft = (new Date(request.dueAt).getTime() - Date.now()) / DAY_MS;
  if (daysLeft < 0) return "red";
  if (daysLeft <= 14) return "orange";
  return "green";
}

function LegacyNoticeLauncher() {
  const dispatch = useDispatch();
  const [preparing, setPreparing] = useState(false);
  const [prepared, setPrepared] = useState(null);

  const prepare = async () => {
    setPreparing(true);
    try {
      const campaign = await dispatch(
        createCampaign({
          subject: "Important: how we use your data",
          message: "",
          status: { type: "warning", description: "Draft" },
          audience: { legacyNotice: true },
        }),
      );
      const preview = await dispatch(getRecipientsPreview(campaign._id));
      setPrepared({ campaign, preview });
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          "Failed to prepare the legacy-notice campaign.",
      );
    } finally {
      setPreparing(false);
    }
  };

  return (
    <Card
      size="small"
      title="Section 5(2) legacy notice"
      extra={<ThunderboltOutlined />}
    >
      <Paragraph type="secondary">
        Creates a draft campaign, pre-filled with your org's DPDP notice
        content, targeted at contacts added before the DPDP cutoff who
        haven't been notified yet (deadline: 13 May 2027). Review and send it
        from Campaigns like any other draft.
      </Paragraph>
      {prepared ? (
        <Alert
          type="success"
          showIcon
          message={`Draft campaign ready — ${prepared.preview?.totalContacts ?? 0} recipient(s).`}
          action={
            <Link to="/app/campaigns">
              <Button size="small" type="primary">
                Go to Campaigns
              </Button>
            </Link>
          }
        />
      ) : (
        <Button
          icon={<PlusOutlined />}
          loading={preparing}
          onClick={prepare}
        >
          Prepare legacy notice campaign
        </Button>
      )}
    </Card>
  );
}

function NewRightsRequestForm({ onCreated }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    if (!values.contactId && !values.emailAddress) {
      message.error("Provide a contact ID or an email address.");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(createRightsRequest(values));
      message.success("Rights request logged.");
      form.resetFields();
      onCreated();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to log the rights request.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="inline" onFinish={onFinish} style={{ rowGap: 12 }}>
      <Form.Item name="type" rules={[{ required: true, message: "Type" }]}>
        <Select
          placeholder="Request type"
          style={{ width: 160 }}
          options={RIGHTS_REQUEST_TYPES}
        />
      </Form.Item>
      <Form.Item name="emailAddress">
        <Input placeholder="Email address" style={{ width: 220 }} />
      </Form.Item>
      <Form.Item name="contactId">
        <Input placeholder="Contact ID (optional)" style={{ width: 180 }} />
      </Form.Item>
      <Form.Item name="notes">
        <Input placeholder="Notes (optional)" style={{ width: 220 }} />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Log request
        </Button>
      </Form.Item>
    </Form>
  );
}

export default function Compliance() {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    dispatch(listRightsRequests(statusFilter ? { status: statusFilter } : {}))
      .then(setRequests)
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [dispatch, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const fulfil = async (record) => {
    try {
      await dispatch(fulfilRightsRequest(record._id));
      message.success("Request fulfilled.");
      load();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to fulfil the request.",
      );
    }
  };

  const setStatus = async (record, status) => {
    try {
      await dispatch(updateRightsRequest(record._id, { status }));
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update status.");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadConsentExportCsv();
    } catch {
      message.error("Failed to export consent CSV.");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: "Contact",
      dataIndex: "emailAddress",
    },
    {
      title: "Received",
      dataIndex: "receivedAt",
      render: (v) => new Date(v).toLocaleDateString(),
    },
    {
      title: "Due",
      dataIndex: "dueAt",
      render: (v, record) => (
        <Tag color={dueColor(record)}>{new Date(v).toLocaleDateString()}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) =>
        record.status === "fulfilled" || record.status === "rejected" ? (
          <Text type="secondary">—</Text>
        ) : (
          <Space>
            {record.status === "open" && (
              <Button size="small" onClick={() => setStatus(record, "in_progress")}>
                Start
              </Button>
            )}
            <Button size="small" type="primary" onClick={() => fulfil(record)}>
              Fulfil
            </Button>
            <Button size="small" danger onClick={() => setStatus(record, "rejected")}>
              Reject
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginTop: 0 }}>
          Compliance
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          DPDP Act tools: the Section 5(2) legacy-notice launcher and the
          data-principal rights-request board (access, correction, erasure,
          withdrawal — 90-day fulfillment SLA).
        </Paragraph>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <LegacyNoticeLauncher />
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Consent export">
            <Paragraph type="secondary">
              Org-wide CSV of every contact's current marketing-consent
              status, for a compliance review.
            </Paragraph>
            <Button loading={exporting} onClick={exportCsv}>
              Export consent CSV
            </Button>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Rights requests"
        extra={
          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "open", label: "Open" },
              { value: "in_progress", label: "In progress" },
              { value: "fulfilled", label: "Fulfilled" },
              { value: "rejected", label: "Rejected" },
            ]}
          />
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <NewRightsRequestForm onCreated={load} />
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={requests}
            pagination={{ pageSize: 10 }}
          />
        </Space>
      </Card>
    </Space>
  );
}
